import { type FC, type ReactNode, useMemo, useRef } from "react";

import { type DraftMarkerLayout, type DraftSection } from "@app_schema";

import { DocumentFrame, DocumentPane } from "@styled/document_frame";
import { LoadingSpinner } from "@styled/loading_spinner";
import { NoticeLine } from "@styled/notice_line";
import { SavedMark } from "@styled/saved_mark";

import { DraftEditLayer } from "./draft_edit_layer";
import { type DraftFill } from "./draft_fill";
import { DraftInsertLayer } from "./draft_insert_layer";
import {
  DRAFT_FRAME_DOCUMENT,
  DRAFT_FRAME_SANDBOX,
  useDraftFrame,
} from "./use_draft_frame";
import { useDraftParagraphEdit } from "./use_draft_paragraph_edit";
import { useDraftingSectionsOf } from "./use_drafting_sections";
import { useSetupSlots } from "./use_setup_slots";
import { useShowDraftSection } from "./use_show_draft_section";

type Section = Pick<DraftSection, "marker" | "lines">;
type MarkerLayout = Pick<
  DraftMarkerLayout,
  "marker" | "separated" | "numbered"
>;

// One line over the report at a time, the one that matters most.
const DraftNotice: FC<{
  failed: boolean;
  notApplied: string | null;
}> = ({ failed, notApplied }) => {
  if (failed) {
    return (
      <NoticeLine>
        Couldn&apos;t show your report. Its pages are in the Template.
      </NoticeLine>
    );
  }
  if (notApplied) return <NoticeLine>{notApplied}</NoticeLine>;
  return null;
};

// His Word report as Word draws it, each drafted section typed in at its marker
// and each one not yet drafted a tinted block. A click on his paragraph or a
// drafted line edits it in place; a gap between paragraphs takes a new section.
export const DraftDocumentView: FC<{
  // Its display copy, as Word draws it.
  file: { id: string; url: string };
  sections: ReadonlyArray<Section>;
  layouts: ReadonlyArray<MarkerLayout>;
  // The section the chat is writing now, and its label over its place.
  writing: { marker: number; label: string | null } | null;
  notApplied: string | null;
  filling: boolean;
  line?: ReactNode;
  hidden: boolean;
  // Insert here adds sections to this case's draft.
  projectId: string;
  // The chat whose sections are typed in, for editing their lines.
  conversationId?: string;
}> = ({
  file,
  sections,
  layouts,
  writing,
  notApplied,
  filling,
  line,
  hidden,
  projectId,
  conversationId,
}) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const fills = useMemo(
    () =>
      sections.flatMap((section): DraftFill[] =>
        section.marker != null
          ? [
              {
                marker: section.marker,
                lines: section.lines,
                layout: layouts.find(
                  (layout) => layout.marker === section.marker,
                ),
              },
            ]
          : [],
      ),
    [sections, layouts],
  );
  const drafting = useDraftingSectionsOf();
  const writingMarker = writing?.marker ?? null;
  const writingLabel = writing?.label;
  const slots = useMemo(
    () =>
      new Map(
        drafting.sections.map((section) => [
          section.marker,
          section.marker === writingMarker && writingLabel
            ? writingLabel
            : section.slotLabel,
        ]),
      ),
    [drafting.sections, writingMarker, writingLabel],
  );
  const { state, root, scrollTo } = useDraftFrame({
    frameRef,
    fileId: file.id,
    url: file.url,
    sections: fills,
    slots,
    writing: writingMarker,
    filling,
  });
  useShowDraftSection(!hidden, scrollTo);
  const { setup } = drafting;
  useSetupSlots({
    frameRef,
    root,
    slots: setup.slots,
    selected: setup.selected,
    onSelect: setup.select,
  });
  const edit = useDraftParagraphEdit(projectId, drafting, {
    conversationId,
    fills,
  });
  // His template's review is up: the Report shows where its sections go, and
  // takes no edits or sections of its own until it's done.
  const ready = state === "ready" && !filling && !hidden && !setup.showing;
  // The layers read his blocks again once the preview's slots are gone.
  const drawn = useMemo(() => [fills, setup.showing], [fills, setup.showing]);

  return (
    <DocumentPane hidden={hidden}>
      {line}
      <DraftNotice failed={state === "failed"} notApplied={notApplied} />
      {state === "ready" && setup.slots.length > 0 && (
        <NoticeLine>
          {setup.proposal?.changed
            ? "Tinted blocks are your new sections. Click one to find it in the list."
            : "Each tinted block is where a section will go. Click one to find it in the list."}
        </NoticeLine>
      )}
      {ready && (
        <NoticeLine>
          {edit.saved && <SavedMark />}
          Click any paragraph to edit it. Edits stay on this case. Hover a gap
          between paragraphs to add a section there.
        </NoticeLine>
      )}
      {state === "loading" && <LoadingSpinner />}
      <DocumentFrame
        title="Your draft"
        sandbox={DRAFT_FRAME_SANDBOX}
        srcDoc={DRAFT_FRAME_DOCUMENT}
        frameRef={frameRef}
        gutter
      />
      <DraftInsertLayer
        frameRef={frameRef}
        root={root}
        drawn={drawn}
        enabled={ready && !edit.editing && !edit.tip}
      />
      <DraftEditLayer
        edit={edit}
        locks={drafting.locks}
        frameRef={frameRef}
        root={root}
        drawn={drawn}
        enabled={ready}
        writing={writingMarker}
      />
    </DocumentPane>
  );
};
